import { graphql, useStaticQuery } from 'gatsby';

import getLinkPath from '../utils/getLinkPath';
import getI18nData from '../utils/i18n/getI18nData';
import { Url } from '../types/drupal-custom';
import { LanguageContext } from '../types/i18n';
import { Products } from '../types/types';
import { Link } from '../types/marketo';

interface MarketoFormLinks {
  drupal: {
    lucidchartQuote?: {
      entityUrl: Url;
    };
    lucidchartDemo?: {
      entityUrl: Url;
    };
    lucidcoQuote?: {
      entityUrl: Url;
    };
    lucidcoDemo?: {
      entityUrl: Url;
    };
    lucideduContact?: {
      entityUrl: Url;
    };
    lucideduPremium?: {
      entityUrl: Url;
    };
    lucideduTemplateFeedback?: {
      entityUrl: Url;
    };
    lucidscaleContact?: {
      entityUrl: Url;
    };
    lucidsparkContact?: {
      entityUrl: Url;
    };
  };
  i18nData?: {
    edges: {
      node: {
        name?: string | null;
        childSrcJson?: {
          [k: string]: string | any;
        };
      };
    }[];
  } | null;
}

type MarketoFormMappingKeys =
  | 'lucidchartQuote'
  | 'lucidchartDemo'
  | 'lucidcoQuote'
  | 'lucidcoDemo'
  | 'lucideduContact'
  | 'lucideduPremium'
  | 'lucideduTemplateFeedback'
  | 'lucidscaleContact'
  | 'lucidsparkContact';

type MarketoFormMappings = {
  [K in MarketoFormMappingKeys]: Link;
};

const getProductMapping = (product: Products.Site, forms: MarketoFormMappings) => {
  const siteProductMapping = {
    chart: new Map([
      // single page get a quote
      ['1120', forms.lucidchartQuote],
      ['1138', forms.lucidchartQuote],
      ['1984', forms.lucidchartQuote],
      ['1980', forms.lucidchartQuote],
      ['1992', forms.lucidchartQuote],
      ['1996', forms.lucidchartQuote],
      ['2166', forms.lucidchartQuote],
      ['1988', forms.lucidchartQuote],
      // legacy get a quote
      ['1055', forms.lucidchartQuote],
      ['1395', forms.lucidchartQuote],
      ['1261', forms.lucidchartQuote],
      ['1266', forms.lucidchartQuote],
      ['1263', forms.lucidchartQuote],
      ['1269', forms.lucidchartQuote],
      ['1366', forms.lucidchartQuote],
      ['2015', forms.lucidchartQuote],
      ['1264', forms.lucidchartQuote],
      // enterprise get a quote - top
      ['1127', forms.lucidchartQuote],
      // enterprise get a quote - bottom
      ['1128', forms.lucidchartQuote],
      ['1982', forms.lucidchartQuote],
      ['1978', forms.lucidchartQuote],
      ['1979', forms.lucidchartQuote],
      ['1990', forms.lucidchartQuote],
      ['1994', forms.lucidchartQuote],
      ['2162', forms.lucidchartQuote],
      ['1986', forms.lucidchartQuote],
      ['1987', forms.lucidchartQuote],
      ['1943', forms.lucidchartQuote],
      // edu get a quote
      ['1056', forms.lucidchartQuote],
      ['1265', forms.lucidchartQuote],
      ['2493', forms.lucidchartQuote],
      // nineteen-hero get a quote
      ['2145', forms.lucidchartQuote],
      ['3175', forms.lucidchartQuote],
      // request a demo
      ['1060', forms.lucidchartDemo],
      ['1394', forms.lucidchartDemo],
      ['1270', forms.lucidchartDemo],
      ['1384', forms.lucidchartDemo],
      ['1379', forms.lucidchartDemo],
      ['1357', forms.lucidchartDemo],
      ['2014', forms.lucidchartDemo],
      ['1364', forms.lucidchartDemo],
      // demo request pages request a demo
      ['2383', forms.lucidchartDemo],
      // nineteen-hero request a demo
      ['2601', forms.lucidchartDemo],
      ['2617', forms.lucidchartDemo],
      ['3176', forms.lucidchartDemo],
    ]),
    chartblog: new Map([['0', null]]),
    co: new Map([['0', null]]),
    edu: new Map([
      ['2493', forms.lucideduPremium],
      ['2884', forms.lucideduTemplateFeedback],
      ['2888', forms.lucideduTemplateFeedback],
      ['3324', forms.lucideduContact],
    ]),
    scale: new Map([
      // single page contact sales
      ['1120', forms.lucidscaleContact],
    ]),
    spark: new Map([
      // single page contact sales
      ['3240', forms.lucidsparkContact],
      ['3241', forms.lucidsparkContact],
      ['3242', forms.lucidsparkContact],
      ['3243', forms.lucidsparkContact],
      ['3244', forms.lucidsparkContact],
      ['3245', forms.lucidsparkContact],
    ]),
  };

  return siteProductMapping[product];
};

const useMarketoLink = (language: Pick<LanguageContext, 'i18nKey' | 'langPrefix'>, product: Products.Site) => {
  const { drupal, i18nData } = useStaticQuery<MarketoFormLinks>(
    graphql`
      {
        drupal {
          lucidchartQuote: nodeById(id: "4045") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucidchartDemo: nodeById(id: "4048") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucidcoQuote: nodeById(id: "220") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucidcoDemo: nodeById(id: "238") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucideduContact: nodeById(id: "611") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucideduPremium: nodeById(id: "634") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucideduTemplateFeedback: nodeById(id: "633") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucidscaleContact: nodeById(id: "18") {
            entityUrl {
              ...getLinkPath
            }
          }
          lucidsparkContact: nodeById(id: "51") {
            entityUrl {
              ...getLinkPath
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_all_get_a_quote
                mktg_ct_all_request_a_demo
                mktg_nav_contact_sales
              }
            }
          }
        }
      }
    `,
  );

  const i18n = getI18nData(i18nData, language.i18nKey);

  const textDefaults: Record<string, string> = {
    lucidchartDemo: i18n('mktg-ct-all-request-a-demo'),
    lucidchartQuote: i18n('mktg-ct-all-get-a-quote'),
    lucidcoDemo: i18n('mktg-ct-all-request-a-demo'),
    lucidcoQuote: i18n('mktg-ct-all-get-a-quote'),
    lucideduContact: i18n('mktg_nav_contact_sales'),
    lucideduPremium: 'See how it works',
    lucideduTemplateFeedback: 'Write a review',
    lucidscaleContact: i18n('mktg_nav_contact_sales'),
    lucidsparkContact: i18n('mktg_nav_contact_sales'),
  };

  const formKeys: Record<string, Link> = {};

  (Object.keys(drupal) as Array<keyof typeof drupal>).forEach(key => {
    formKeys[key] = {
      url: getLinkPath(drupal[key]?.entityUrl, language.langPrefix),
      copy: textDefaults[key],
    };
  });

  const forms = {
    lucidchartDemo: formKeys['lucidchartDemo'],
    lucidchartQuote: formKeys['lucidchartQuote'],
    lucidcoDemo: formKeys['lucidcoDemo'],
    lucidcoQuote: formKeys['lucidcoQuote'],
    lucideduContact: formKeys['lucideduContact'],
    lucideduPremium: formKeys['lucideduPremium'],
    lucideduTemplateFeedback: formKeys['lucideduTemplateFeedback'],
    lucidscaleContact: formKeys['lucidscaleContact'],
    lucidsparkContact: formKeys['lucidsparkContact'],
  };

  const defaultValues = {
    chart: forms.lucidchartQuote,
    chartblog: forms.lucidchartQuote,
    co: forms.lucidcoQuote,
    edu: forms.lucideduContact,
    scale: forms.lucidscaleContact,
    spark: forms.lucidsparkContact,
  };

  return (marketoId?: string | null) => {
    let value = defaultValues[product];
    const singleMarketoFormPages = getProductMapping(product, forms);

    if (marketoId) {
      if (!singleMarketoFormPages.has(marketoId)) {
        throw new Error(`Invalid Marketo Id: ${marketoId}`);
      }
      value = singleMarketoFormPages.get(marketoId)!;
    }

    return value;
  };
};

export default useMarketoLink;
